// When an access token stops being good, on this tab's own clock (B1119). The server's `expiresAt` is an instant on
// the server's clock, so comparing it with the tab's `Date.now()` is off by however far the computer's clock is from the
// server's: about nine minutes fast, and the back office renewed every five seconds (the timer's floor) for as long as
// the tab was open, rotating the refresh family each time; slow, and it renewed after the token had expired, so calls
// met a 401 first. The token carries the server's own `iat` and `exp`, so its lifetime (`exp - iat`) is the server's
// measure whatever either clock says, and the tab counts that lifetime from the moment it received the token. Nothing
// here is trusted for access: the server verifies every token on every call; this only decides when to ask for a new one.

/** The `iat` and `exp` (epoch seconds, the server's clock) of a compact JWS, or null when it isn't one. */
function lifetimeSeconds(token: string): number | null {
  const payload = token.split('.')[1];
  if (payload === undefined || payload === '') return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')), (c) => c.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (typeof claims !== 'object' || claims === null) return null;
    const { iat, exp } = claims as { readonly iat?: unknown; readonly exp?: unknown };
    if (typeof iat !== 'number' || typeof exp !== 'number' || !Number.isInteger(iat) || !Number.isInteger(exp) || exp <= iat) return null;
    return exp - iat;
  } catch {
    return null;
  }
}

/**
 * The instant, on this tab's clock, at which a token received at `receivedAt` (this tab's `Date.now()` when the reply
 * arrived) expires: `receivedAt` plus the token's lifetime as the server minted it. The token was minted a moment before
 * the reply arrived (and `iat` is floored to the second), so this runs up to a round trip and a second late; every
 * caller renews a minute early. A token that carries no lifetime falls back to the server's `expiresAt`.
 */
export function localExpiry(token: string, expiresAt: number, receivedAt: number): number {
  const seconds = lifetimeSeconds(token);
  return seconds === null ? expiresAt : receivedAt + seconds * 1000;
}
